/**
 * usePermissions — React hook providing capability-based permission checks.
 */
import { useMemo } from 'react';
import { useGymRole } from '../hooks/useGymRole';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { resolveGymCaps, resolvePlatformCaps } from '@sendly/shared';

/**
 * @param {string|null} uid - auth user's UID
 * @param {object|null} userDoc - users/{uid} document data
 * @returns {{ can: (cap: string) => boolean, canAny: (...caps: string[]) => boolean, canAll: (...caps: string[]) => boolean, gymCaps: Set<string>, platformCaps: Set<string> }}
 */
export function usePermissions() {
  const { memberData } = useGymRole();
  const { userData: userDoc } = useCurrentUser();

  const gymCaps = useMemo(
    () => resolveGymCaps(memberData, userDoc),
    [memberData, userDoc]
  );

  const platformCaps = useMemo(
    () => resolvePlatformCaps(userDoc),
    [userDoc]
  );

  return useMemo(() => ({
    can: (cap) => gymCaps.has(cap) || platformCaps.has(cap),
    canAny: (...caps) => caps.some(c => gymCaps.has(c) || platformCaps.has(c)),
    canAll: (...caps) => caps.every(c => gymCaps.has(c) || platformCaps.has(c)),
    gymCaps,
    platformCaps,
  }), [gymCaps, platformCaps]);
}
